import _formatErrorMessage from "@mui/x-internals/formatErrorMessage";
import _extends from "@babel/runtime/helpers/esm/extends";
import { warn } from '@base-ui/utils/warn';
import { warnOnce } from '@mui/x-internals/warning';
import { EMPTY_OBJECT } from '@base-ui/utils/empty';
import { DEFAULT_SCHEDULER_PREFERENCES, SchedulerStore } from '@mui/x-scheduler-internals/internals';
import { createChangeEventDetails } from '@mui/x-scheduler-internals/base-ui-copy';
import { EventTimelinePremiumLazyLoadingPlugin } from "./plugins/EventTimelinePremiumLazyLoadingPlugin.mjs";
import { schedulerRecurringEventsPlugin } from "../internals/plugins/schedulerRecurringEventsPlugin.mjs";
import { EVENT_TIMELINE_PREMIUM_PRESET_CONFIGS, getPresetPxPerDay } from "../internals/utils/preset-utils.mjs";

// Sorted by descending px/day (most zoomed-in first). Each preset's `(timeResolution,
// tickWidth)` must produce a unique px/day — otherwise the order is decided by
// `Object.keys` insertion order, which is not a stable contract.
const PRESET_ZOOM_ORDER = Object.keys(EVENT_TIMELINE_PREMIUM_PRESET_CONFIGS).sort((a, b) => getPresetPxPerDay(b) - getPresetPxPerDay(a));
export const DEFAULT_PRESETS = PRESET_ZOOM_ORDER;
export const DEFAULT_PRESET = PRESET_ZOOM_ORDER[0];
export const DEFAULT_SHOULD_EVENT_REQUIRE_RESOURCE = true;
function sortPresetsByZoomOrder(presets) {
  if (process.env.NODE_ENV !== 'production') {
    if (presets.length === 0) {
      // TODO: fix mui/no-guarded-throw
      // eslint-disable-next-line mui/no-guarded-throw
      throw new Error(`MUI X Scheduler: EventTimelinePremium received an empty \`presets\` prop. ` + `This leaves the timeline without any preset to render. ` + `Pass at least one preset, or omit the prop to use the default set (${PRESET_ZOOM_ORDER.join(', ')}). ` + `See https://mui.com/x/react-scheduler/event-timeline/presets/ for more details.`);
    }
    const unknown = presets.filter(preset => !PRESET_ZOOM_ORDER.includes(preset));
    if (unknown.length > 0) {
      // TODO: fix mui/no-guarded-throw
      // eslint-disable-next-line mui/no-guarded-throw
      throw new Error(`MUI X Scheduler: EventTimelinePremium received unknown preset(s) in the \`presets\` prop: ${unknown.join(', ')}. ` + `These entries have no associated configuration, so the timeline cannot render them. ` + `Remove the unknown preset(s), or use one of the built-in values (${PRESET_ZOOM_ORDER.join(', ')}). ` + `See https://mui.com/x/react-scheduler/event-timeline/presets/ for more details.`);
    }
  }
  // Iterating over `PRESET_ZOOM_ORDER` (instead of the input) yields a canonical,
  // duplicate-free output even when runtime inputs (storage, URL params, dynamic
  // registries) bypass the compile-time `EventTimelinePremiumPreset` union.
  return PRESET_ZOOM_ORDER.filter(preset => presets.includes(preset));
}
const deriveStateFromParameters = parameters => ({
  presets: sortPresetsByZoomOrder(parameters.presets ?? DEFAULT_PRESETS)
});
export const DEFAULT_PREFERENCES = DEFAULT_SCHEDULER_PREFERENCES;
function warnIfShouldEventRequireResourceMisconfigured(shouldEventRequireResource, resources) {
  if (shouldEventRequireResource && (resources == null || resources.length === 0)) {
    warnOnce(['MUI X Scheduler: `shouldEventRequireResource` is `true` but no resources are configured.', 'Users will not be able to select a resource, and events cannot be saved from the event dialog.', 'Either provide at least one resource, or set `shouldEventRequireResource={false}`.']);
  }
}
const mapper = {
  getInitialState: (schedulerInitialState, parameters) => {
    const shouldEventRequireResource = parameters.shouldEventRequireResource ?? DEFAULT_SHOULD_EVENT_REQUIRE_RESOURCE;
    warnIfShouldEventRequireResourceMisconfigured(shouldEventRequireResource, parameters.resources);
    return _extends({}, schedulerInitialState, deriveStateFromParameters(parameters), {
      preset: parameters.preset ?? parameters.defaultPreset ?? DEFAULT_PRESET,
      preferences: parameters.preferences ?? parameters.defaultPreferences ?? EMPTY_OBJECT,
      shouldEventRequireResource,
      hasInitialized: false
    });
  },
  updateStateFromParameters: (newSchedulerState, parameters, updateModel) => {
    const shouldEventRequireResource = parameters.shouldEventRequireResource ?? DEFAULT_SHOULD_EVENT_REQUIRE_RESOURCE;
    warnIfShouldEventRequireResourceMisconfigured(shouldEventRequireResource, parameters.resources);
    const newState = _extends({}, newSchedulerState, deriveStateFromParameters(parameters), {
      shouldEventRequireResource,
      hasInitialized: true
    });
    updateModel(newState, 'preset', 'defaultPreset');
    updateModel(newState, 'preferences', 'defaultPreferences');
    return newState;
  }
};
export class EventTimelinePremiumStore extends SchedulerStore {
  constructor(parameters, adapter) {
    super(parameters, adapter, 'EventTimelinePremiumStore', mapper, schedulerRecurringEventsPlugin);
    if (process.env.NODE_ENV !== 'production') {
      // Assert the initial state validity; `subscribe` only fires on subsequent state changes.
      this.assertPresetValidity(this.state.preset);
      this.subscribe(state => {
        this.assertPresetValidity(state.preset);
        return null;
      });
    }
    this.lazyLoading = new EventTimelinePremiumLazyLoadingPlugin(this);
  }
  assertPresetValidity(preset) {
    const presets = this.state.presets;
    if (!presets.includes(preset)) {
      throw new Error(process.env.NODE_ENV !== "production" ? `MUI X Scheduler: EventTimelinePremium received the preset "${preset}", which is not part of the \`presets\` prop (received: ${presets.join(', ')}). ` + `This leaves the timeline in an inconsistent state where the current preset is not one of the allowed options. ` + `Add "${preset}" to the \`presets\` prop, or pass a preset that is already included. ` + `See https://mui.com/x/react-scheduler/event-timeline/presets/ for more details.` : _formatErrorMessage(259, preset, presets.join(', '), preset));
    }
  }
  buildPublicAPI() {
    return _extends({}, super.buildPublicAPI(), {
      goToNextVisibleDate: this.goToNextVisibleDate,
      goToPreviousVisibleDate: this.goToPreviousVisibleDate
    });
  }

  /**
   * Goes to the next visible date span based on the current preset.
   */
  goToNextVisibleDate = event => {
    const {
      adapter,
      visibleDate,
      preset
    } = this.state;
    const {
      unitCount,
      navigate
    } = EVENT_TIMELINE_PREMIUM_PRESET_CONFIGS[preset];
    this.setVisibleDate({
      visibleDate: navigate(adapter, visibleDate, unitCount),
      event
    });
  };

  /**
   * Goes to the previous visible date span based on the current preset.
   */
  goToPreviousVisibleDate = event => {
    const {
      adapter,
      visibleDate,
      preset
    } = this.state;
    const {
      unitCount,
      navigate
    } = EVENT_TIMELINE_PREMIUM_PRESET_CONFIGS[preset];
    this.setVisibleDate({
      visibleDate: navigate(adapter, visibleDate, -unitCount),
      event
    });
  };

  /**
   * Sets the preset of the timeline.
   */
  setPreset = (preset, event) => {
    const {
      preset: presetProp,
      onPresetChange
    } = this.parameters;
    if (process.env.NODE_ENV !== 'production' && presetProp !== undefined && !onPresetChange) {
      warn('MUI X Scheduler: EventTimelinePremium is controlled (received a `preset` prop) but `onPresetChange` is not provided. Preset changes will be silently ignored.');
    }
    if (preset !== this.state.preset) {
      this.assertPresetValidity(preset);
      const eventDetails = createChangeEventDetails('none', event);
      onPresetChange?.(preset, eventDetails);
      if (!eventDetails.isCanceled && presetProp === undefined) {
        this.set('preset', preset);
      }
    }
  };
}